/**
 * The planning list's rows: one per route per day, built from the plan and the generated days.
 *
 * A planned route is a template; the list shows what it means on each date in the range. Where a
 * route instance exists for that date it stands in for the template, because its times and stops
 * are the ones the day will actually run with. Where none exists yet the template is laid onto
 * the date.
 *
 * A row standing in for a recurring route's date carries the id `<plannedId>-YYYY-MM-DD`, so
 * every row in the list has its own id; `planningBaseRouteId` takes the suffix back off.
 */
import {
  addDays,
  eachDayOfInterval,
  endOfDay,
  format,
  getDay,
  isAfter,
  isBefore,
  isWithinInterval,
  parseISO,
  startOfDay,
} from "date-fns"

import { operatorOn } from "@/lib/operatorAllocations"
import { priceRoute, resolveRouteKind } from "@/lib/routeCostCalculation"
import {
  DayOfWeek,
  Location,
  PlannedRoute,
  PlannedStop,
  RouteInstance,
  RoutePlanned,
  StopInstance,
} from "@/types/collections"

/** A route's priced day, as priceRoute returns it. */
export type PlanningRouteCost = NonNullable<ReturnType<typeof priceRoute>>

/** A `collections_routes_planned` row as the list's legacy `PlannedRoute` shape. */
export function toPlannedRoute(rp: RoutePlanned): PlannedRoute {
  return {
    id: rp.id,
    code: rp.code,
    category: rp.category,
    date: parseISO(rp.effective_from),
    plannedStartTime: new Date(`${rp.effective_from}T${rp.default_start_time}`),
    plannedEndTime: new Date(`${rp.effective_from}T${rp.default_end_time}`),
    stops:
      rp.stops?.map((stop) => ({
        id: stop.id,
        routeId: rp.id,
        sequence: stop.sequence,
        type: "collection" as const,
        locationId: stop.location_id,
        slotId: stop.availability_slot_id,
        name: stop.location?.site_name || "",
        address: stop.location?.address || "",
        postcode: stop.location?.postcode || "",
        lat: stop.location?.latitude || 0,
        lng: stop.location?.longitude || 0,
        plannedArrival: new Date(`${rp.effective_from}T${stop.planned_arrival}`),
        plannedDeparture: new Date(`${rp.effective_from}T${stop.planned_departure}`),
        serviceTimeMinutes: stop.service_time_minutes,
        // Volume comes from stop instances, which the list reads per stop.
        volumeEstimated: 0,
        loadingUnitsEstimated: 0,
        slotType: stop.stop_type,
        stop_type: stop.stop_type,
        haulageDeliveryRevenueGbp: stop.haulage_delivery_revenue_gbp ?? undefined,
        directInjectionCostGbp: stop.direct_injection_cost_gbp ?? undefined,
      })) || [],
    totalStops: rp.stops?.length || 0,
    depot: rp.depot,
    estimatedCost: rp.estimated_cost,
    isRecurring: rp.days && rp.days.length > 0,
    recurringDays: rp.days,
    recurringEndDate: rp.effective_to ? parseISO(rp.effective_to) : undefined,
    excludedDates: (rp.excluded_dates ?? []).map((d: string) => parseISO(d)),
    status: rp.status,
    createdAt: new Date(rp.created_at),
    updatedAt: new Date(rp.updated_at),
    operator: rp.operator,
    operatorAllocations: rp.operator_allocations,
    vehicleType: rp.vehicle_type,
    // Doubles the wage line on a double-manned route
    crewSize: rp.crew_size,
    // Segments and prices a stand-by day differently
    route_kind: rp.route_kind,
  }
}

/** The planned route id behind a list row, with any `-YYYY-MM-DD` date suffix removed. */
export function planningBaseRouteId(id: string): string {
  return id.includes("-") && id.match(/-\d{4}-\d{2}-\d{2}$/)
    ? id.replace(/-\d{4}-\d{2}-\d{2}$/, "")
    : id
}

/** An instance's date as YYYY-MM-DD, read off the string where it is one. */
const instanceDateOnly = (d: string | Date): string =>
  typeof d === "string" ? d.split("T")[0] : format(new Date(d), "yyyy-MM-dd")

/**
 * The one instance that stands for a route on a day, out of those that match.
 *
 * A date can hold a live instance beside cancelled history: that is what a day called off and
 * then planned again looks like. So this has to choose, and whichever row the API listed first
 * is not a choice -- taking the dead copy drew a running route as cancelled and kept it off the
 * run list. The live one wins. A cancelled one stands only where it is all there is, which is a
 * day that really is off. The generator makes the same choice (existingInstances.ts).
 */
function pickInstance(
  instances: RouteInstance[] | undefined,
  matches: (instance: RouteInstance) => boolean,
): RouteInstance | undefined {
  let cancelled: RouteInstance | undefined
  for (const instance of instances ?? []) {
    if (!matches(instance)) continue
    if (instance.status !== "cancelled") return instance
    cancelled ??= instance
  }
  return cancelled
}

/** The generated day for a planned route on a date, if there is one. */
export function findRouteInstance(
  instances: RouteInstance[] | undefined,
  baseRouteId: string,
  dateStr: string,
): RouteInstance | undefined {
  return pickInstance(
    instances,
    (r) => r.planned_route_id === baseRouteId && instanceDateOnly(r.date) === dateStr,
  )
}

/** The day's own record of a stop: the instance stop at the same location, if there is one. */
export function instanceStopFor(
  instance: RouteInstance | undefined,
  stop: { locationId?: string },
): StopInstance | undefined {
  if (!stop?.locationId) return undefined
  return instance?.stops?.find((s: StopInstance) => s.location_id === stop.locationId)
}

/**
 * A stop's forecast volume, from the instance stop at the same location.
 *
 * Read per stop from the instance rather than summed per location, which would inflate when the
 * same location appears on more than one stop.
 */
export function instanceStopForecast(
  instance: RouteInstance | undefined,
  stop: { locationId?: string },
): number {
  return (instanceStopFor(instance, stop)?.volume_forecast ?? 0) as number
}

const DAY_OF_WEEK: Record<number, DayOfWeek> = {
  0: "sunday",
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
}

export function dayOfWeekOf(date: Date): DayOfWeek {
  return DAY_OF_WEEK[getDay(date)]
}

/** A list row from a route instance, so the list shows the day's own times and stops. */
export function toDisplayRouteFromInstance(
  route: PlannedRoute,
  ri: RouteInstance,
  displayId: string,
): PlannedRoute {
  const instanceStops = (ri.stops ?? [])
    .slice()
    .sort((a: StopInstance, b: StopInstance) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .map((s: StopInstance) => ({
      id: s.id,
      routeId: route.id,
      sequence: s.sequence ?? 0,
      // Matches the other instance-stop mappings: collections throughout,
      // with the finer distinction on slotType.
      type: "collection" as const,
      locationId: s.location_id,
      name: s.name ?? "",
      address: s.address ?? "",
      postcode: s.postcode ?? "",
      lat: Number(s.lat ?? 0),
      lng: Number(s.lng ?? 0),
      plannedArrival: s.planned_arrival ? new Date(s.planned_arrival) : new Date(),
      plannedDeparture: s.planned_departure ? new Date(s.planned_departure) : new Date(),
      serviceTimeMinutes: s.service_time_minutes ?? 30,
      volumeEstimated: s.volume_forecast ?? 0,
      loadingUnitsEstimated: s.loading_units_forecast ?? 0,
      slotType:
        ((s as { stop_type?: string }).stop_type as PlannedStop["slotType"]) ??
        "shipper_collection",
      slotServices: ((s as { slot_services?: string[] }).slot_services ??
        []) as PlannedStop["slotServices"],
      slotPnl: ((s as { slot_pnl?: unknown }).slot_pnl ?? []) as PlannedStop["slotPnl"],
      haulageDeliveryRevenueGbp:
        (s as { haulage_delivery_revenue_gbp?: number }).haulage_delivery_revenue_gbp ??
        undefined,
      directInjectionCostGbp:
        (s as { direct_injection_cost_gbp?: number }).direct_injection_cost_gbp ?? undefined,
    }))
  return {
    ...route,
    id: displayId,
    routeInstanceId: ri.id,
    routeInstanceStarted: !!ri.actual_start_time,
    routeInstanceStatus: ri.status,
    date: parseISO(ri.date),
    // Assignment is a fact about the day, so it arrives with the instance or not at all.
    driverId: ri.driver_id ?? undefined,
    driverName: ri.driver_name ?? undefined,
    vehicleReg: ri.vehicle_reg ?? undefined,
    // Who runs it is a fact about the day too: one date can be handed to another operator.
    operator: ri.operator || route.operator,
    plannedStartTime: ri.planned_start_time
      ? new Date(ri.planned_start_time)
      : route.plannedStartTime,
    plannedEndTime: ri.planned_end_time ? new Date(ri.planned_end_time) : route.plannedEndTime,
    stops: instanceStops,
    totalStops: instanceStops.length,
  }
}

/**
 * Every route on every date it runs within the range, with an instance standing in where one
 * exists and has stops.
 *
 * Without a complete range the routes come back as they are, one row each on their start date.
 * Excluded dates are skipped, and a recurring route runs from its start date to its end date (or
 * the end of the range) on its recurring days only.
 */
export function expandPlannedRoutes(
  routes: PlannedRoute[],
  range: { from?: Date; to?: Date } | undefined,
  routeInstances: RouteInstance[] | undefined,
): PlannedRoute[] {
  if (!range?.from || !range?.to) return routes

  const expanded: PlannedRoute[] = []
  const rangeStart = startOfDay(range.from)
  const rangeEnd = startOfDay(range.to)
  const toDateOnly = (d: string | Date) => format(new Date(d), "yyyy-MM-dd")

  for (const route of routes) {
    if (!route.isRecurring || !route.recurringDays || route.recurringDays.length === 0) {
      const routeDate = startOfDay(route.date)
      if (isWithinInterval(routeDate, { start: rangeStart, end: endOfDay(range.to) })) {
        const excludedDates = route.excludedDates ?? []
        const isExcluded = excludedDates.some(
          (ex: Date) => startOfDay(ex).getTime() === routeDate.getTime(),
        )
        if (!isExcluded) {
          const dateStr = format(route.date, "yyyy-MM-dd")
          const ri = pickInstance(
            routeInstances,
            (r) => r.planned_route_id === route.id && toDateOnly(r.date) === dateStr,
          )
          const operator = operatorOn(route, route.date)
          expanded.push(
            ri && ri.stops?.length
              ? toDisplayRouteFromInstance(route, ri, route.id)
              : operator === route.operator
                ? route
                : { ...route, operator },
          )
        }
      }
      continue
    }

    const routeStartDate = startOfDay(route.date)
    const routeEndDate = route.recurringEndDate ? startOfDay(route.recurringEndDate) : rangeEnd

    const iterateStart = isBefore(rangeStart, routeStartDate) ? routeStartDate : rangeStart
    const iterateEnd = isAfter(rangeEnd, routeEndDate) ? routeEndDate : rangeEnd

    if (!isAfter(iterateStart, iterateEnd)) {
      const daysInRange = eachDayOfInterval({ start: iterateStart, end: iterateEnd })

      const excludedDates = route.excludedDates ?? []
      const isExcluded = (d: Date) =>
        excludedDates.some((ex: Date) => startOfDay(ex).getTime() === startOfDay(d).getTime())

      for (const day of daysInRange) {
        const dayOfWeek = dayOfWeekOf(day)
        if (route.recurringDays.includes(dayOfWeek) && !isExcluded(day)) {
          const dateStr = format(day, "yyyy-MM-dd")
          const displayId = `${route.id}-${dateStr}`
          const ri = pickInstance(
            routeInstances,
            (r) => r.planned_route_id === route.id && toDateOnly(r.date) === dateStr,
          )
          if (ri && ri.stops?.length) {
            expanded.push(toDisplayRouteFromInstance(route, ri, displayId))
            continue
          }
          // No instance: create entry from planned template (same calendar day for all stop
          // times). The route's own end rolls to the next morning when it is at or before the
          // start, so an overnight route's duration and window read right.
          const templateStart = new Date(`${dateStr}T${format(route.plannedStartTime, "HH:mm:ss")}`)
          let templateEnd = new Date(`${dateStr}T${format(route.plannedEndTime, "HH:mm:ss")}`)
          if (templateEnd <= templateStart) templateEnd = addDays(templateEnd, 1)
          expanded.push({
            ...route,
            id: displayId,
            date: day,
            // Beyond the generated days there is no instance to say who runs it.
            operator: operatorOn(route, day),
            plannedStartTime: templateStart,
            plannedEndTime: templateEnd,
            stops: route.stops.map((stop) => ({
              ...stop,
              plannedArrival: new Date(`${dateStr}T${format(stop.plannedArrival, "HH:mm:ss")}`),
              plannedDeparture: new Date(`${dateStr}T${format(stop.plannedDeparture, "HH:mm:ss")}`),
            })),
          })
        }
      }
    }
  }

  return expanded
}

export type PlanningSortColumn =
  | "code"
  | "status"
  | "timeWindow"
  | "startDepot"
  | "endDepot"
  | "stops"
  | "volume"
  | "loadFactor"
  | "shiftCover"
  | "emptyPct"
  | "cost"
  | "recurring"

/** The per-route figures a sort needs that are not on the route itself. */
export interface RouteSortFigures {
  volume: number
  /** The planning load factor; absent when the route has no capacity to measure. */
  fillPercentage: number | null | undefined
  emptyPct: number | null | undefined
  coverRank: number
  costTotal: number | undefined
}

/** A route's comparable value for a column. Routes with no figure sort as -1 (or 0 for cost). */
export function getRouteSortValue(
  route: PlannedRoute,
  column: string,
  figures: RouteSortFigures,
  getLocation: (locationId: string) => Pick<Location, "site_name"> | undefined,
): string | number {
  switch (column) {
    case "code":
      return (route.code ?? "").toLowerCase()
    case "status":
      return route.status ?? ""
    case "timeWindow":
      return route.plannedStartTime?.getTime() ?? 0
    case "startDepot": {
      const firstStop = route.stops?.find((s) => s.sequence === 1) || route.stops?.[0]
      const name = firstStop
        ? (getLocation(firstStop.locationId)?.site_name ?? route.depot ?? "")
        : (route.depot ?? "")
      return (name ?? "").toLowerCase()
    }
    case "endDepot": {
      const lastStop = route.stops?.length ? route.stops[route.stops.length - 1] : null
      const name = lastStop
        ? (getLocation(lastStop.locationId)?.site_name ?? route.depot ?? "")
        : (route.depot ?? "")
      return (name ?? "").toLowerCase()
    }
    case "stops":
      return route.totalStops ?? 0
    case "volume":
      return figures.volume
    case "loadFactor":
      return figures.fillPercentage ?? -1
    case "emptyPct":
      return figures.emptyPct ?? -1
    case "shiftCover":
      return figures.coverRank
    case "cost":
      return figures.costTotal ?? 0
    case "recurring":
      return route.isRecurring ? 1 : 0
    default:
      return ""
  }
}

/**
 * One day's routes in list order: sorted by the column, with the stand-by ones held at the bottom.
 *
 * With no column the incoming order is kept. `valueOf` is read once per route.
 */
export function sortDayRoutes(
  routes: PlannedRoute[],
  column: string | null,
  direction: "asc" | "desc",
  valueOf: (route: PlannedRoute, column: string) => string | number,
): PlannedRoute[] {
  const dir = direction === "asc" ? 1 : -1
  const values = new Map<PlannedRoute, string | number>()
  if (column) routes.forEach((route) => values.set(route, valueOf(route, column)))

  return [...routes].sort((a, b) => {
    /**
     * Stand-by sinks below every real run, whatever the column sort says.
     *
     * Sorting them in with the rest is the original complaint in a different form: a spare
     * driver's day sitting between two collection routes, ranked by a cost or a stop count
     * that means something different for it, still reads as a route. Held at the bottom it
     * reads as what it is -- what is left after the day's work is laid out.
     *
     * Ahead of the column comparison rather than folded into it, so it survives sorting by
     * any column and in either direction.
     */
    const aStandby = resolveRouteKind(a.route_kind) === "standby"
    const bStandby = resolveRouteKind(b.route_kind) === "standby"
    if (aStandby !== bStandby) return aStandby ? 1 : -1

    // No column chosen: leave the existing order alone. Array#sort is stable, so 0 here
    // preserves it rather than shuffling.
    if (!column) return 0

    const va = values.get(a) as string | number
    const vb = values.get(b) as string | number
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir
    return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir
  })
}
